import { useTranslation } from 'react-i18next';
import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  Tooltip,
  TooltipTrigger,
  TooltipContent,
  TooltipProvider,
  UserCard,
  OrgFunctionCard,
  SystemCard,
  ExternalContactCard,
  cn,
  isAssigneeDeleted,
  appendDeletedSuffix,
  DELETED_ENTITY_CLASS
} from '@bliks-io/ui';
import { ENTITY_ICONS, type AppIcon } from '@/constants/entityIcons';
import { orgFunctionDetailRoute } from '@/lib/orgFunctionRoutes';
import { MaybeLink } from './MaybeLink';
import { useAuthStore } from '@/stores/auth-store';
import type { OrgFunctionKind, PopulatedExternalContact } from '@bliks-io/contracts';
import type {
  TrackedUser,
  TrackedTeam,
  TrackedSystem,
  TrackedArtifact,
  EntityUsage,
  EntityUsageRole
} from '../ProcessDetailPage';

interface ProcessInvolvedEntitiesProps {
  people: TrackedUser[];
  /** OrgFunction assignees of `kind: 'team'`. */
  teams: TrackedTeam[];
  /** OrgFunction assignees of `kind: 'role'` (Funktionen). */
  functions: TrackedTeam[];
  systems: TrackedSystem[];
  artifacts: TrackedArtifact[];
  externalContacts: PopulatedExternalContact[];
}

/** Shared width so every entity group wraps uniformly in the flex layout. */
const GROUP_CLASS = 'flex-1 min-w-[220px]';

/**
 * Empty state placeholder with dashed border
 */
function EmptyState({ text }: { text: string }) {
  return (
    <div className="flex items-center justify-center h-16 border border-dashed border-border-11 rounded-lg bg-[#FAFAFA]">
      <span className="text-sm text-gray-8">{text}</span>
    </div>
  );
}

/** Group header: entity icon + title + count. */
function SectionHeader({
  icon: Icon,
  title,
  count
}: {
  icon: AppIcon;
  title: string;
  count: number;
}) {
  return (
    <div className="flex items-center gap-2 mb-3">
      <Icon className="h-4 w-4 text-gray-8" />
      <h3 className="text-sm font-medium text-gray-8">
        {title} ({count})
      </h3>
    </div>
  );
}

/**
 * Tooltip content showing entity usage grouped by role
 */
function UsageTooltipContent({ usages }: { usages: EntityUsage[] }) {
  const { t } = useTranslation();

  // Group usages by role
  const byRole = usages.reduce(
    (acc, u) => {
      acc[u.role] = acc[u.role] || [];
      if (!acc[u.role].includes(u.stepName)) {
        acc[u.role].push(u.stepName);
      }
      return acc;
    },
    {} as Record<EntityUsageRole, string[]>
  );

  return (
    <div className="space-y-2 text-sm max-w-xs">
      {byRole.assigned && byRole.assigned.length > 0 && (
        <div>
          <div className="font-medium">{t('processes.usageAssigned')}</div>
          <ul className="list-disc list-inside text-xs text-muted-foreground">
            {byRole.assigned.map((s, i) => (
              <li key={i}>{s}</li>
            ))}
          </ul>
        </div>
      )}
      {byRole.recorded && byRole.recorded.length > 0 && (
        <div>
          <div className="font-medium">{t('processes.usageRecorded')}</div>
          <ul className="list-disc list-inside text-xs text-muted-foreground">
            {byRole.recorded.map((s, i) => (
              <li key={i}>{s}</li>
            ))}
          </ul>
        </div>
      )}
      {byRole.whoElseDoesThis && byRole.whoElseDoesThis.length > 0 && (
        <div>
          <div className="font-medium">{t('processes.usageWhoElseDoesThis')}</div>
          <ul className="list-disc list-inside text-xs text-muted-foreground">
            {byRole.whoElseDoesThis.map((s, i) => (
              <li key={i}>{s}</li>
            ))}
          </ul>
        </div>
      )}
      {byRole.helper && byRole.helper.length > 0 && (
        <div>
          <div className="font-medium">{t('processes.usageHelper')}</div>
          <ul className="list-disc list-inside text-xs text-muted-foreground">
            {byRole.helper.map((s, i) => (
              <li key={i}>{s}</li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

/**
 * A group of OrgFunction assignees. Teams (`kind: 'team'`) and Funktionen
 * (`kind: 'role'`) render the same card — only the header glyph/title and the
 * detail route differ by `kind`.
 */
function OrgFunctionGroup({
  icon,
  title,
  items,
  kind,
  slug
}: {
  icon: AppIcon;
  title: string;
  items: TrackedTeam[];
  kind: OrgFunctionKind;
  slug: string;
}) {
  return (
    <div className={GROUP_CLASS}>
      <SectionHeader icon={icon} title={title} count={items.length} />
      <div className="flex flex-col gap-2">
        <TooltipProvider>
          {items.map((item) => (
            <Tooltip key={item._id}>
              <TooltipTrigger asChild>
                <MaybeLink
                  to={orgFunctionDetailRoute(kind)}
                  params={{ slug, orgFunctionId: item._id }}
                  className="block"
                  disabled={isAssigneeDeleted(item)}
                >
                  <OrgFunctionCard orgFunction={item} variant="name-only" itemVariant="outline" />
                </MaybeLink>
              </TooltipTrigger>
              <TooltipContent side="right">
                <UsageTooltipContent usages={item.usages} />
              </TooltipContent>
            </Tooltip>
          ))}
        </TooltipProvider>
      </div>
    </div>
  );
}

export function ProcessInvolvedEntities({
  people,
  teams,
  functions,
  systems,
  artifacts,
  externalContacts
}: ProcessInvolvedEntitiesProps) {
  const { t } = useTranslation();
  const slug = useAuthStore().slug();

  return (
    <Card className="mb-6">
      <CardHeader>
        <CardTitle>{t('processes.involvedEntities')}</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="flex flex-wrap gap-6">
          {/* People */}
          <div className={GROUP_CLASS}>
            <SectionHeader
              icon={ENTITY_ICONS.members}
              title={t('processes.involvedPeople')}
              count={people.length}
            />
            <div className="flex flex-col gap-2">
              {people.length > 0 ? (
                <TooltipProvider>
                  {people.map((person) => (
                    <Tooltip key={person._id}>
                      <TooltipTrigger asChild>
                        <MaybeLink
                          to="/$slug/org-members/$orgMemberId"
                          params={{ slug, orgMemberId: person._id }}
                          className="block"
                          disabled={isAssigneeDeleted(person)}
                        >
                          <UserCard
                            user={{
                              firstName: person.firstName,
                              lastName: person.lastName,
                              email: person.email || '',
                              role:
                                person.mainRole && typeof person.mainRole === 'object'
                                  ? person.mainRole
                                  : undefined,
                              avatar: person.avatar,
                              departmentAbbreviation:
                                person.department && typeof person.department === 'object'
                                  ? person.department.abbreviation
                                  : undefined,
                              // Re-stamped from the lookup during aggregation
                              deletedAt: person.deletedAt,
                              status: person.status
                            }}
                            variant="with-role"
                            itemVariant="outline"
                          />
                        </MaybeLink>
                      </TooltipTrigger>
                      <TooltipContent side="right">
                        <UsageTooltipContent usages={person.usages} />
                      </TooltipContent>
                    </Tooltip>
                  ))}
                </TooltipProvider>
              ) : (
                <EmptyState text={t('processes.noInvolvedPeople')} />
              )}
            </div>
          </div>

          {/* Teams (kind: 'team') */}
          {teams.length > 0 && (
            <OrgFunctionGroup
              icon={ENTITY_ICONS.team}
              title={t('processes.involvedTeams')}
              items={teams}
              kind="team"
              slug={slug}
            />
          )}

          {/* Funktionen (kind: 'role') */}
          {functions.length > 0 && (
            <OrgFunctionGroup
              icon={ENTITY_ICONS.orgFunction}
              title={t('processes.involvedFunctions')}
              items={functions}
              kind="role"
              slug={slug}
            />
          )}

          {/* Systems */}
          <div className={GROUP_CLASS}>
            <SectionHeader
              icon={ENTITY_ICONS.system}
              title={t('processes.usedSystems')}
              count={systems.length}
            />
            <div className="flex flex-col gap-2">
              {systems.length > 0 ? (
                <TooltipProvider>
                  {systems.map((system) => (
                    <Tooltip key={system._id}>
                      <TooltipTrigger asChild>
                        <MaybeLink
                          to="/$slug/systems/$systemId"
                          params={{ slug, systemId: system._id }}
                          className="block"
                          disabled={isAssigneeDeleted(system)}
                        >
                          <SystemCard
                            system={system}
                            variant="with-category"
                            itemVariant="outline"
                          />
                        </MaybeLink>
                      </TooltipTrigger>
                      <TooltipContent side="right">
                        <UsageTooltipContent usages={system.usages} />
                      </TooltipContent>
                    </Tooltip>
                  ))}
                </TooltipProvider>
              ) : (
                <EmptyState text={t('processes.noUsedSystems')} />
              )}
            </div>
          </div>

          {/* Artifacts */}
          <div className={GROUP_CLASS}>
            <SectionHeader
              icon={ENTITY_ICONS.artifact}
              title={t('processes.usedArtifacts')}
              count={artifacts.length}
            />
            <div className="flex flex-col gap-2">
              {artifacts.length > 0 ? (
                <TooltipProvider>
                  {artifacts.map((artifact) => {
                    const isDeleted = isAssigneeDeleted(artifact);
                    return (
                      <Tooltip key={artifact._id}>
                        <TooltipTrigger asChild>
                          <MaybeLink
                            to="/$slug/artifacts/$artifactId"
                            params={{ slug, artifactId: artifact._id }}
                            className={cn(
                              'flex items-center gap-2 p-2 rounded-lg border border-border-11 bg-white',
                              isDeleted && DELETED_ENTITY_CLASS
                            )}
                            disabled={isDeleted}
                          >
                            <ENTITY_ICONS.artifact className="h-4 w-4 text-[#636369]" />
                            <span className="text-sm font-medium">
                              {appendDeletedSuffix(artifact.name, isDeleted, t)}
                            </span>
                          </MaybeLink>
                        </TooltipTrigger>
                        <TooltipContent side="right">
                          <UsageTooltipContent usages={artifact.usages} />
                        </TooltipContent>
                      </Tooltip>
                    );
                  })}
                </TooltipProvider>
              ) : (
                <EmptyState text={t('processes.noUsedArtifacts')} />
              )}
            </div>
          </div>

          {/* External Contacts */}
          <div className={GROUP_CLASS}>
            <SectionHeader
              icon={ENTITY_ICONS.externalContact}
              title={t('processes.externalContacts')}
              count={externalContacts.length}
            />
            <div className="flex flex-col gap-2">
              {externalContacts.length > 0 ? (
                externalContacts.map((contact) => (
                  <MaybeLink
                    key={contact._id}
                    to="/$slug/external-contacts/$externalContactId"
                    params={{ slug, externalContactId: contact._id }}
                    className="block"
                    disabled={isAssigneeDeleted(contact)}
                  >
                    <ExternalContactCard
                      contact={contact}
                      variant="with-type"
                      itemVariant="outline"
                    />
                  </MaybeLink>
                ))
              ) : (
                <EmptyState text={t('processes.noExternalContacts')} />
              )}
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
